// Authority 05: player authority adapter over the existing Combat PRO spell
// runtime. This module owns no spell database, target store, dice engine or HP
// mutation. It validates a character's assigned spell entry, calls the protected
// spell preparation/save kernels, and returns bounded operations for the shared
// CombatContext handlers to apply exactly once.

import { damageResult, applyHealing } from '../damage/damageEngine';
import { abilityModifier } from '../players/playerEngine';
import { getSpellData } from './spellEngine';
import { prepareSpellExecution, resolveSave, trackDuration } from './spellCastEngine';

export function playerSpellEntries(player) {
  const source = player && Array.isArray(player.spells) ? player.spells : [];
  return source.map(function (entry) {
    if (typeof entry === 'string') {
      return { name: entry, active: true, maxUses: null, usesRemaining: null, castRound: null };
    }
    return Object.assign({ active: true, maxUses: null, usesRemaining: null, castRound: null }, entry || {});
  }).filter(function (entry) { return Boolean(entry.name && String(entry.name).trim()); });
}

export function authorisedPlayerSpell(player, spellIndex, spellsDb) {
  const entries = playerSpellEntries(player);
  const entry = entries[spellIndex];
  if (!entry || entry.active === false) { return null; }
  if (entry.usesRemaining !== null && entry.usesRemaining !== undefined && Number(entry.usesRemaining) <= 0) { return null; }
  const spell = getSpellData(entry.name, spellsDb || []);
  if (!spell) { return null; }
  return { entry: entry, spell: prepareSpellExecution(spell, player) || spell };
}

function playerTarget(state, token) {
  const prefix = typeof token === 'string' ? token.slice(0, 2) : '';
  if (prefix !== 'p:' && prefix !== 'c:') { return null; }
  const id = parseInt(token.slice(2), 10);
  const players = state && Array.isArray(state.players) ? state.players : [];
  const index = players.findIndex(function (player) { return player.id === id; });
  if (index < 0) { return null; }
  const player = players[index];
  if (prefix === 'c:' && !player.companion) { return null; }
  return { kind: 'player', index: index, id: id, token: token, value: player, name: player.name || ('Player ' + (index + 1)) };
}

function monsterTarget(state, token) {
  if (typeof token !== 'number' && !/^\d+$/.test(String(token))) { return null; }
  const index = Number(token);
  const monsters = state && Array.isArray(state.monsters) ? state.monsters : [];
  const monster = monsters[index];
  if (!monster) { return null; }
  return {
    kind: 'monster', index: index, token: index, value: monster,
    name: monster.customName || monster.selectedMonster || ('Monster ' + (index + 1)),
  };
}

export function resolveCombatTarget(state, token) {
  return playerTarget(state, token) || monsterTarget(state, token);
}

function targetSaveModifier(target, saveType) {
  const key = String(saveType || '').toLowerCase();
  if (!key) { return 0; }
  if (target.kind === 'monster') {
    return target.value.saves ? Number(target.value.saves[key] || 0) : 0;
  }
  return abilityModifier(target.value[key] == null ? 10 : target.value[key]);
}

function resultHp(target, amount, healing) {
  const current = target.kind === 'monster' ? Number(target.value.currentHP || 0) : Number(target.value.hp || 0);
  const maximum = target.kind === 'monster' ? Number(target.value.maxHP || current) : Number(target.value.maxHp || current);
  return healing ? applyHealing(current, maximum, amount) : damageResult(current, amount).newHP;
}

export function resolvePlayerSpellCast(options) {
  const opts = options || {};
  const state = opts.state || {};
  const player = (state.players || []).find(function (candidate) { return candidate.id === opts.playerId; });
  const authorised = authorisedPlayerSpell(player, opts.spellIndex, opts.spellsDb || []);
  if (!player || !authorised) { return { ok: false, reason: 'UNAUTHORISED_SPELL', operations: [], results: [] }; }

  const spell = authorised.spell;
  let tokens = Array.isArray(opts.targets) ? opts.targets.slice() : [];
  if (spell.target === 'self') { tokens = ['p:' + player.id]; }
  const seen = {};
  tokens = tokens.filter(function (token) {
    const key = String(token);
    if (seen[key]) { return false; }
    seen[key] = true;
    return true;
  });
  if (!tokens.length) { return { ok: false, reason: 'TARGET_REQUIRED', operations: [], results: [] }; }

  const operations = [];
  const results = [];
  const rollD20 = opts.rollD20;
  const rollFormula = opts.rollFormula;
  const casterDc = Number(player.spellDC || 13);
  const attackModifier = Number(player.spellAttackModifier || 0);

  tokens.forEach(function (token) {
    const target = resolveCombatTarget(state, token);
    if (!target) {
      results.push({ token: token, state: 'INVALID_TARGET' });
      return;
    }
    let saved = null;
    let attack = null;
    let applies = true;
    if (spell.saveType) {
      const save = resolveSave(rollD20(), targetSaveModifier(target, spell.saveType), casterDc);
      saved = save;
      applies = !save.saved || spell.saveEffect === 'half';
    } else if (spell.attackType && spell.attackType !== 'save') {
      const rolled = Number(rollD20()) + attackModifier;
      attack = { total: rolled, ac: Number(target.value.ac || 10), hit: rolled >= Number(target.value.ac || 10) };
      applies = attack.hit;
    }

    let amount = 0;
    let effect = null;
    if (applies && spell.damageFormula) {
      const rolled = rollFormula(spell.damageFormula);
      amount = Number(rolled && rolled.total ? rolled.total : 0);
      if (saved && saved.saved && spell.saveEffect === 'half') { amount = Math.max(1, Math.floor(amount / 2)); }
      effect = spell.spellType === 'heal' ? 'healing' : 'damage';
      operations.push({ kind: effect, target: target, amount: amount });
    }
    if (applies && spell.appliesCondition && !(saved && saved.saved)) {
      operations.push({ kind: 'condition', target: target, condition: spell.appliesCondition });
    }
    results.push({
      token: target.token, target: target.name,
      attack: attack, save: saved, effect: effect, amount: amount,
      hpAfter: effect ? resultHp(target, amount, effect === 'healing') : null,
      condition: applies && !(saved && saved.saved) ? (spell.appliesCondition || null) : null,
      state: applies ? 'APPLIED' : 'NO_EFFECT',
    });
  });

  return {
    ok: results.some(function (result) { return result.state !== 'INVALID_TARGET'; }),
    spell: spell, entry: authorised.entry, operations: operations, results: results,
    currentRound: Number(state.currentRound || 1),
  };
}

export function deriveActivePlayerSpells(player, currentRound, state) {
  const active = player && Array.isArray(player.activeSpells) ? player.activeSpells : [];
  return active.map(function (record) {
    let status = record.status || 'ACTIVE';
    const remaining = trackDuration({ durationRounds: record.durationRounds }, record.castRound, currentRound);
    if (status === 'ACTIVE' && typeof remaining === 'number' && remaining <= 0) { status = 'EXPIRED'; }
    if (status === 'ACTIVE') {
      const invalid = (record.targets || []).some(function (token) { return !resolveCombatTarget(state, token); });
      if (invalid) { status = 'TARGET_INVALID'; }
    }
    if (record.concentration && player.concentrationCastId !== record.id && status === 'ACTIVE') { status = 'INTERRUPTED'; }
    return Object.assign({}, record, { status: status, roundsRemaining: remaining });
  });
}
